<script lang="ts" setup>
import { useBuilderStore } from '@/store/builder'
import {
  Copy01Icon,
  Trash02Icon,
  ImageUpIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { computed, PropType, reactive, ref } from 'vue'
import {
  IElement,
  ELEMENTS_LOOKUP,
  DragHandleSVG,
  AddToContentLibraryModal,
  featureFlags,
} from '@gohighlevel/ghl-proposals-common'
import { UITooltip } from '@gohighlevel/ghl-ui'
import { useProductListStore } from '@/store/productListStore'
import { useActionBarStore } from '@/store/actionBar'
import { useAppStore } from '@/store/app'
import { useRoute } from 'vue-router'
import { createElement } from '@/helper/element-factory'
import { useDocument } from '../../composition'
const emit = defineEmits(['dragstart', 'dragend'])
const props = defineProps({
  elementId: {
    type: String,
    required: true,
  },
  element: {
    type: Object as PropType<IElement>,
  },
})

const route = useRoute()
const appStore = useAppStore()
const locationId = computed(() => route.params.locationId)
const store = useBuilderStore()
const productListStore = useProductListStore()
const actionBarStore = useActionBarStore()
const showAddToContentLibraryModal = ref(false)
const pageElementForContentLib = ref()
const { documentVersion } = useDocument()

const activeElementId = computed(() => store.activeElementId)
const state = reactive({
  isActiveElement: computed(() => {
    if (!props.elementId) return false
    return activeElementId.value === props.elementId
  }),
  activeElementStyle: {},
})

state.activeElementStyle = computed(() => {
  const style: any = {}
  if (state.isActiveElement) {
    // style.boxShadow = 'rgb(130, 145, 148) 0px 0px 4px'
  } else {
    // style.backgroundColor = 'rgba(0, 145, 174, 0.1)'
  }
  return style
})

function del(e: any) {
  e.stopPropagation()
  const children = store.getElementById(props.elementId)?.element
    ?.children as IElement['children']
  if (children?.length > 0) {
    const groupIds = children?.reduce((acc: Set<string>, child) => {
      if (child.type === ELEMENTS_LOOKUP.CHECKBOX) {
        const group = store.getElementGroup(child.id)
        if (group) {
          acc.add(group.id)
        }
      }
      return acc
    }, new Set<string>())
    groupIds.forEach(groupId => {
      store.deleteGroup(groupId)
    })
    store.deleteElement(props.elementId)
  } else {
    store.deleteElement(props.elementId)
  }

  if (props.element?.type === ELEMENTS_LOOKUP.SIGNATURE) {
    store.syncRecipient()
  }

  if (props.element?.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
    const allLineItemsLength = store.totalLineItemsLength
    if (allLineItemsLength <= 0) {
      productListStore.setProductListCurrency('')
      productListStore.resetSchedule()
    }
  }
}

function clone(e: any) {
  e.stopPropagation()
  store.cloneElement(props.elementId)
}

const isHovered = computed(
  () => store.focusedElement?.id === props.elementId && store.hover
)
const isTableElement = computed(
  () => store.activeElement?.type === ELEMENTS_LOOKUP.TABLE
)

const onDragStart = (ev: MouseEvent) => {
  emit('dragstart', ev, props.element)
  actionBarStore.setLastActiveDragElement(props.element as IElement)
}

const onDragEnd = (ev: MouseEvent) => {
  emit('dragend', ev, props.element)
  actionBarStore.setLastActiveDragElement(null)
}

const addToContentLib = () => {
  const pageElement = createElement({ type: ELEMENTS_LOOKUP.PAGE })
  if (props.element) pageElement?.children.push(props.element)
  pageElementForContentLib.value = pageElement
  openAddToContentLibraryModal(true)
}

const openAddToContentLibraryModal = (val: boolean) => {
  showAddToContentLibraryModal.value = val
}

const showContentLibrary = computed(
  () =>
    ((featureFlags.CONTENT_LIBRARY_ACCESSIBLE_TO_PUBLIC ?? false) ||
      appStore.isInternal) &&
    documentVersion.value > 1
)
</script>

<template>
  <div
    v-if="store.isDocumentEditable && state.isActiveElement"
    class="actions absolute top-[-8px] left-[45%] -mt-8 flex z-20 overlay-actions animate-slideUp"
    :class="{
      '!left-[0%]': isTableElement,
    }"
  >
    <div
      class="flex justify-between bg-gray-charcoal border border-gray-700 shadow-float p-[4px] rounded-sm"
    >
      <UITooltip>
        <template #trigger>
          <div
            class="hover:bg-gray-600 w-full cursor-pointer flex"
            @click="clone"
          >
            <Copy01Icon class="text-white p-1 text-sm h-4 w-4" />
          </div>
        </template>
        {{ $t('common.copy') }}
      </UITooltip>
      <UITooltip>
        <template #trigger>
          <div
            class="hover:bg-gray-600 w-full cursor-pointer flex"
            @click="del"
          >
            <Trash02Icon class="text-white p-1 text-sm h-4 w-4" />
          </div>
        </template>
        {{ $t('common.delete') }}
      </UITooltip>
      <UITooltip v-if="showContentLibrary">
        <template #trigger>
          <div
            class="hover:bg-gray-600 w-full cursor-pointer flex"
            @click="addToContentLib"
          >
            <ImageUpIcon class="text-white p-1 text-sm h-4 w-4" />
          </div>
        </template>
        {{ $t('contentLibrary.addToContentLib') }}
      </UITooltip>
    </div>
  </div>
  <div
    v-if="isHovered || state.isActiveElement"
    :style="state.activeElementStyle"
    class="absolute pointer-events-none overlay-container rounded-smooth"
    style="inset: 0px; z-index: 2"
    :class="{
      'border border-[#4af]': state.isActiveElement,
      border: isHovered,
    }"
  ></div>
  <div
    @mousedown="onDragStart"
    @mouseup="onDragEnd"
    v-if="state.isActiveElement || isHovered"
    class="handle remove-selecto-selection absolute h-[40px] w-2 px-[8px] left-[-28px] top-0 rounded-smooth cursor-move flex justify-center items-center pointer-events-auto bg-[#FFF] shadow-page border ease-linear"
  >
    <div
      class="remove-selecto-selection transform rotate-90 pointer-events-auto text-white font-sans"
      style="font-size: 12px; line-height: 0.2; z-index: 3"
    >
      <DragHandleSVG class="remove-selecto-selection rotate-90" />
    </div>
  </div>

  <AddToContentLibraryModal
    :show="showAddToContentLibraryModal"
    @on-cancel="openAddToContentLibraryModal(false)"
    @on-success="openAddToContentLibraryModal(false)"
    :isFromPage="false"
    :pageElement="pageElementForContentLib"
    :groups="store.groups"
    :locationId="locationId"
    :element="props.element"
    :isInternal="appStore.isInternal"
    :companyId="appStore.companyId"
  />
</template>

<style>
/* override parent hover state on child active/ focus state state */
.el-active > div > .overlay-container {
  /* border: 1px solid #98a2b3 !important; */
}

/* handle parent move handle  */
.el-active > div > .overlay-container .handle {
  display: none;
}

.el-active > div > .overlay-actions {
  display: none;
}

.el-active > div > .overlay-container {
  background-color: transparent !important;
}
</style>
